import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  MessageCircle,
  Send,
  User,
  Phone,
  ArrowRight,
  Shield,
  Activity,
  Heart,
  Clock,
  MapPin
} from 'lucide-react';
import './SupportChat.css';

const BASE_URL = 'https://dr-mohamed-khaled.site';
const DOCTOR_ID = '5b075375-0b21-4e67-91c8-1ab2c709fa85';

const normalizeMessages = (raw) =>
  raw.map((m) => ({
    id: m.id ?? m._id ?? String(Math.random()),
    from:
      m.senderType === 'patient'
        ? 'user'
        : m.senderType === 'doctor' || m.senderType === 'support' || m.senderType === 'assistant'
        ? 'support'
        : m.sender_type === 'patient'
        ? 'user'
        : m.sender_type === 'doctor' || m.sender_type === 'support'
        ? 'support'
        : m.role === 'patient' || m.role === 'user'
        ? 'user'
        : 'support',
    text: m.message ?? m.text ?? '',
  }));

const SupportChat = () => {
  const [phase, setPhase] = useState('register'); // 'register' | 'chat'
  const [messages, setMessages] = useState([]);
  const [inputVal, setInputVal] = useState('');
  const [patientName, setPatientName] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [roomId, setRoomId] = useState(null);
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input
  useEffect(() => {
    if (phase === 'chat' && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [phase]);

  // Fetch Messages
  const fetchRoomMessages = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await axios.get(`${BASE_URL}/api/chat/rooms/${id}`);
      const raw =
        data?.room?.messages ||
        data?.messages ||
        data?.data?.messages ||
        [];
      if (!Array.isArray(raw)) return;
      setMessages(normalizeMessages(raw));
    } catch (_) {
      // silent on errors
    }
  }, []);

  // Poll messages every 4s
  useEffect(() => {
    if (roomId && phase === 'chat') {
      pollRef.current = setInterval(() => fetchRoomMessages(roomId), 4000);
    }
    return () => clearInterval(pollRef.current);
  }, [roomId, phase, fetchRoomMessages]);

  const findExistingRoom = async (phone) => {
    try {
      const { data } = await axios.get(`${BASE_URL}/api/chat/rooms`);
      const rooms =
        data?.rooms ?? data?.data ?? (Array.isArray(data) ? data : []);
      const match = rooms.find(
        (r) => r.patient_phone === phone && r.doctor_id === DOCTOR_ID
      );
      return match?.id ?? match?._id ?? null;
    } catch (_) {
      return null;
    }
  };

  const handleStartChat = async (e) => {
    if (e) e.preventDefault();
    const name = patientName.trim();
    const phone = patientPhone.trim();
    if (!name || !phone) return;

    setIsCreatingRoom(true);
    try {
      let id = null;
      try {
        const { data } = await axios.post(`${BASE_URL}/api/chat/rooms/`, {
          doctor_id: DOCTOR_ID,
          patient_name: name,
          patient_phone: phone,
        });
        id = data?.room?.id ?? data?.id ?? data?.data?.id;
      } catch (postErr) {
        const isDuplicate =
          postErr?.response?.data?.error?.toLowerCase().includes('duplicate') ||
          postErr?.response?.status === 409 ||
          postErr?.response?.status === 400;

        if (isDuplicate) {
          id = await findExistingRoom(phone);
        }
      }

      if (!id) throw new Error('Could not join room');

      setRoomId(id);
      setPhase('chat');
      await fetchRoomMessages(id);
    } catch (err) {
      setMessages([
        {
          id: 'err-' + Date.now(),
          from: 'error',
          text: 'تعذّر الاتصال بمركز الخدمة. يرجى التحقق من اتصالك بالإنترنت وإدخال بيانات صحيحة.',
        },
      ]);
    } finally {
      setIsCreatingRoom(false);
    }
  };

  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    const text = inputVal.trim();
    if (!text || !roomId || isSending) return;

    setInputVal('');
    setIsSending(true);
    setIsLoading(true);

    try {
      await axios.post(`${BASE_URL}/api/chat/messages`, {
        roomId,
        senderType: 'patient',
        message: text,
      });
      await fetchRoomMessages(roomId);
    } catch (_) {
      setMessages((prev) => [
        ...prev,
        {
          id: 'err-' + Date.now(),
          from: 'error',
          text: 'لم نتمكن من إرسال الرسالة. يرجى المحاولة مرة أخرى.',
        },
      ]);
    } finally {
      setIsSending(false);
      setIsLoading(false);
    }
  };

  const canStart = patientName.trim().length > 0 && patientPhone.trim().length > 0;

  return (
    <div className="support-chat-page">
      {/* Background decoration */}
      <div className="chat-bg-glow"></div>

      <div className="chat-container-main">
        {/* Main Panel */}
        <div className="chat-panel-box">
          {/* Chat Header */}
          <div className="chat-header-bar">
            <div className="header-doctor-info">
              <div className="doctor-avatar-circle">
                <Heart size={20} className="avatar-heart" />
                <span className="online-indicator-dot"></span>
              </div>
              <div className="doctor-name-meta">
                <h2>فريق الدعم الفني والطبي</h2>
                <p>د. محمد خالد توفيق</p>
              </div>
            </div>
            <div className="header-status-badge">
              <span className="badge-dot"></span>
              <span>متاح الآن للرد</span>
            </div>
          </div>

          {/* ── REGISTER SCREEN ── */}
          {phase === 'register' && (
            <div className="chat-register-screen">
              <div className="register-welcome-info">
                <MessageCircle size={48} className="register-chat-icon" />
                <h3>مرحباً بكِ في المحادثة المباشرة</h3>
                <p>
                  يسعدنا الإجابة عن استفساراتكِ الطبية وتنسيق مواعيدكِ. يرجى إدخال اسمكِ ورقم هاتفكِ لبدء المحادثة الفورية الآمنة مع فريق العيادة.
                </p>
              </div>

              <form onSubmit={handleStartChat} className="register-form">
                <div className="input-group-chat">
                  <label>
                    <User size={14} /> الاسم الكامل
                  </label>
                  <input
                    type="text"
                    placeholder="أدخلي اسمكِ الثلاثي"
                    value={patientName}
                    onChange={(e) => setPatientName(e.target.value)}
                    required
                  />
                </div>

                <div className="input-group-chat">
                  <label>
                    <Phone size={14} /> رقم الهاتف
                  </label>
                  <input
                    type="tel"
                    placeholder="مثال: 01XXXXXXXXX"
                    value={patientPhone}
                    onChange={(e) => setPatientPhone(e.target.value)}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className={`btn-start-chat ${!canStart || isCreatingRoom ? 'disabled' : ''}`}
                  disabled={!canStart || isCreatingRoom}
                >
                  {isCreatingRoom ? 'جاري الاتصال والتحقق...' : 'بدء المحادثة الفورية المباشرة'}
                  <ArrowRight size={18} />
                </button>
              </form>
            </div>
          )}

          {/* ── ACTIVE CHAT SCREEN ── */}
          {phase === 'chat' && (
            <div className="chat-messages-area-wrapper">
              <div className="chat-messages-log">
                {messages.length === 0 && !isLoading && (
                  <div className="empty-chat-tip">
                    <p>أهلاً بكِ {patientName}. كيف يمكننا مساعدتكِ اليوم؟</p>
                    <span>أرسلي استفساركِ وسيقوم أحد أعضاء الفريق الطبي بالرد عليكِ فوراً.</span>
                  </div>
                )}

                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`message-bubble ${
                      msg.from === 'error'
                        ? 'error'
                        : msg.from === 'support'
                        ? 'support'
                        : 'user'
                    }`}
                  >
                    <div className="bubble-content-text">{msg.text}</div>
                  </div>
                ))}

                {isLoading && (
                  <div className="loading-bubbles">
                    <span className="dot-pulse"></span>
                    <span className="dot-pulse"></span>
                    <span className="dot-pulse"></span>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Chat Input row */}
              <form onSubmit={handleSendMessage} className="chat-input-bar-form">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="اكتبي رسالتكِ أو استفساركِ هنا..."
                  value={inputVal}
                  onChange={(e) => setInputVal(e.target.value)}
                  disabled={isSending}
                />
                <button
                  type="submit"
                  className={`btn-send-message ${!inputVal.trim() || isSending ? 'disabled' : ''}`}
                  disabled={!inputVal.trim() || isSending}
                >
                  <Send size={18} />
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Sidebar info */}
        <div className="chat-support-info-sidebar">
          <div className="sidebar-info-card">
            <h3>قنوات اتصال بديلة</h3>
            <div className="sidebar-info-item">
              <Phone size={16} />
              <div>
                <span>طوارئ التوليد (24/7)</span>
                <strong>01289444600</strong>
              </div>
            </div>
            <div className="sidebar-info-item">
              <MessageCircle size={16} />
              <div>
                <span>فرع الشروق (واتساب)</span>
                <strong>01104044537</strong>
              </div>
            </div>
            <div className="sidebar-info-item">
              <MessageCircle size={16} />
              <div>
                <span>فرع مدينة نصر (واتساب)</span>
                <strong>01140147994</strong>
              </div>
            </div>
          </div>

          <div className="sidebar-info-card secure">
            <Shield size={20} className="secure-icon" />
            <h4>محادثة مشفرة وآمنة</h4>
            <p>جميع البيانات والاستشارات الطبية التي يتم مشاركتها سرية تماماً وتخضع لخصوصية المريض.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportChat;
